(() => {
  const REVIEWS_API_BASE =
    "https://ipsujffqzgzfdgsekzsn.supabase.co/functions/v1";
  const LAYOUT_CLASSES = [
    "sl-reviews--list",
    "sl-reviews--grid",
    "sl-reviews--full",
    "sl-reviews--card",
    "sl-reviews--minimal",
    "sl-reviews--grid-2",
    "sl-reviews--grid-3",
    "sl-reviews--masonry",
    "sl-reviews--editorial",
    "sl-reviews--feed-card",
  ];
  const VALID_LAYOUTS = new Set([
    "full",
    "card",
    "minimal",
    "grid-2",
    "grid-3",
    "masonry",
  ]);
  const text = (tag, value, className) => {
    const node = document.createElement(tag);
    node.textContent = value;
    if (className) node.className = className;
    return node;
  };

  const readFlag = (root, key, fallback = true) => {
    const raw = root.dataset[key];
    if (raw === undefined || raw === "") return fallback;
    return raw !== "false";
  };

  const stars = (rating, filled = true) => {
    const value = Math.max(0, Math.min(5, Number(rating) || 0));
    if (!filled) return "★★★★★";
    const full = Math.round(value);
    return "★★★★★".slice(0, full) + "☆☆☆☆☆".slice(0, 5 - full);
  };

  const fractionalStars = (rating) => {
    const value = Math.max(0, Math.min(5, Number(rating) || 0));
    const wrap = document.createElement("span");
    wrap.className = "sl-reviews__stars sl-reviews__stars--fractional";
    wrap.setAttribute("aria-hidden", "true");
    for (let i = 1; i <= 5; i += 1) {
      const star = document.createElement("span");
      star.className = "sl-reviews__star";
      const fill = Math.max(0, Math.min(1, value - (i - 1)));
      star.style.setProperty("--sl-star-fill", String(fill));
      star.textContent = "★";
      wrap.append(star);
    }
    return wrap;
  };

  const resolveLayout = (root, settings) => {
    // Display / API settings are canonical; block dataset is a legacy fallback.
    const fromSettings = settings?.layout;
    if (fromSettings && VALID_LAYOUTS.has(fromSettings)) return fromSettings;
    if (fromSettings === "list" || fromSettings === "grid") return "full";
    const fromBlock = root.dataset.slLayout;
    if (fromBlock && VALID_LAYOUTS.has(fromBlock)) return fromBlock;
    return "full";
  };

  const isMasonryMode = (root, settings) => {
    if (root.dataset.slWidget === "masonry") return true;
    const layout = resolveLayout(root, settings || null);
    return layout === "masonry";
  };

  const applyLayoutClass = (root, layout, settings) => {
    // Compact / carousel / pinned masonry shells keep their own chrome.
    const widget = root.dataset.slWidget;
    if (
      root.classList.contains("sl-reviews--carousel") ||
      root.classList.contains("sl-reviews--summary") ||
      root.classList.contains("sl-reviews--badge") ||
      root.classList.contains("sl-reviews--badge-root") ||
      widget === "carousel" ||
      widget === "stars" ||
      widget === "badge" ||
      widget === "summary"
    ) {
      return;
    }
    if (widget === "masonry") {
      root.classList.remove(...LAYOUT_CLASSES);
      root.classList.add("sl-reviews--masonry", "sl-reviews--feed-card");
      return;
    }
    root.classList.remove(...LAYOUT_CLASSES);
    root.classList.add(`sl-reviews--${layout}`);
    const feedStyle =
      settings?.feed_style === "card" ||
      layout === "card" ||
      layout === "masonry"
        ? "card"
        : "editorial";
    root.classList.toggle("sl-reviews--editorial", feedStyle === "editorial");
    root.classList.toggle("sl-reviews--feed-card", feedStyle === "card");
  };

  const applySettings = (root, settings) => {
    const layout = resolveLayout(root, settings || {});
    applyLayoutClass(root, layout, settings || {});

    if (settings) {
      if (settings.primary_color)
        root.style.setProperty("--sl-reviews-ink", settings.primary_color);
      if (settings.accent_color)
        root.style.setProperty("--sl-reviews-accent", settings.accent_color);
      if (settings.surface_color)
        root.style.setProperty("--sl-reviews-surface", settings.surface_color);
      if (settings.border_color)
        root.style.setProperty("--sl-reviews-line", settings.border_color);
      if (settings.muted_color)
        root.style.setProperty("--sl-reviews-muted", settings.muted_color);
      if (settings.star_size)
        root.style.setProperty(
          "--sl-reviews-star-size",
          `${settings.star_size}px`,
        );
      if (settings.corner_radius != null)
        root.style.setProperty(
          "--sl-reviews-radius",
          `${Number(settings.corner_radius)}px`,
        );
      if (settings.padding != null)
        root.style.setProperty(
          "--sl-reviews-padding",
          `${Number(settings.padding)}px`,
        );
      if (settings.content_width != null) {
        const contentWidth = Number(settings.content_width);
        // 1440 is the Display slider max and means "fill theme width".
        if (Number.isFinite(contentWidth) && contentWidth > 0 && contentWidth < 1440) {
          root.style.setProperty("--sl-reviews-max-width", `${contentWidth}px`);
        } else {
          root.style.removeProperty("--sl-reviews-max-width");
        }
      }
      if (settings.heading_size != null)
        root.style.setProperty(
          "--sl-reviews-heading-size",
          `${Number(settings.heading_size)}px`,
        );
      if (settings.title_size != null)
        root.style.setProperty(
          "--sl-reviews-title-size",
          `${Number(settings.title_size)}px`,
        );
      if (settings.body_size != null)
        root.style.setProperty(
          "--sl-reviews-body-size",
          `${Number(settings.body_size)}px`,
        );
      if (settings.item_gap != null)
        root.style.setProperty(
          "--sl-reviews-item-gap",
          `${Number(settings.item_gap)}px`,
        );
      if (settings.card_padding != null)
        root.style.setProperty(
          "--sl-reviews-card-padding",
          `${Number(settings.card_padding)}px`,
        );
      if (settings.border_width != null)
        root.style.setProperty(
          "--sl-reviews-border-width",
          `${Number(settings.border_width)}px`,
        );
      if (settings.section_margin != null)
        root.style.setProperty(
          "--sl-reviews-section-margin",
          `${Number(settings.section_margin)}px`,
        );
      if (settings.photo_size != null)
        root.style.setProperty(
          "--sl-reviews-photo-size",
          `${Number(settings.photo_size)}px`,
        );
      if (settings.columns_mobile === "1" || settings.columns_mobile === "2")
        root.dataset.slColumnsMobile = String(settings.columns_mobile);
      if (
        settings.line_height === "compact" ||
        settings.line_height === "normal" ||
        settings.line_height === "relaxed"
      )
        root.dataset.slLineHeight = settings.line_height;
      root.dataset.slShowDates =
        settings.show_dates === false ? "false" : "true";
      root.dataset.slBlockBorder =
        settings.show_block_border === true ? "true" : "false";
      root.dataset.slShowPhotos =
        settings.show_photos === false ? "false" : "true";
      root.dataset.slShowWrite =
        settings.show_write_review === false ? "false" : "true";
      root.dataset.slShowAuthor =
        settings.show_author === false ? "false" : "true";
      root.dataset.slShowTitles =
        settings.show_review_titles === false ? "false" : "true";
    }

    const heading = root.querySelector("[data-sl-reviews-heading]");
    if (heading && settings?.section_heading)
      heading.textContent = settings.section_heading;

    const eyebrow = root.querySelector("[data-sl-reviews-eyebrow]");
    if (eyebrow) {
      const textValue = String(settings?.eyebrow || "").trim();
      if (textValue) {
        eyebrow.hidden = false;
        eyebrow.textContent = textValue;
      } else {
        eyebrow.hidden = true;
        eyebrow.textContent = "";
      }
    }

    const writeLabel = String(
      settings?.write_button_label || "Write a review",
    ).trim();
    const writeButtons = root.querySelectorAll("[data-sl-write-review]");
    writeButtons.forEach((writeButton) => {
      const showWrite = settings
        ? settings.show_write_review !== false
        : readFlag(root, "slShowWrite", true);
      writeButton.hidden = !showWrite;
      if (writeLabel) writeButton.textContent = writeLabel;
      writeButton.classList.toggle(
        "is-solid",
        settings?.button_style === "solid",
      );
      writeButton.classList.toggle("is-text", settings?.button_style === "text");
    });
  };

  const truncateBody = (root, bodyText) => {
    const body = text("p", bodyText, "sl-reviews__card-body");
    const limit = 320;
    if (bodyText.length <= limit) return body;
    body.classList.add("is-clamped");
    body.dataset.fullText = bodyText;
    body.textContent = `${bodyText.slice(0, limit).trim()}…`;
    const toggle = text(
      "button",
      root.dataset.readMoreLabel || "Read more",
      "sl-reviews__read-more",
    );
    toggle.type = "button";
    toggle.addEventListener("click", () => {
      const expanded = body.classList.toggle("is-expanded");
      if (expanded) {
        body.classList.remove("is-clamped");
        body.textContent = body.dataset.fullText || bodyText;
        toggle.textContent = root.dataset.showLessLabel || "Show less";
      } else {
        body.classList.add("is-clamped");
        body.textContent = `${bodyText.slice(0, limit).trim()}…`;
        toggle.textContent = root.dataset.readMoreLabel || "Read more";
      }
    });
    const wrap = document.createElement("div");
    wrap.className = "sl-reviews__body-wrap";
    wrap.append(body, toggle);
    return wrap;
  };

  const openLightbox = (root, urls, startIndex = 0) => {
    let index = startIndex;
    const overlay = document.createElement("div");
    overlay.className = "sl-reviews__lightbox";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    const img = document.createElement("img");
    img.className = "sl-reviews__lightbox-img";
    const close = text("button", "×", "sl-reviews__lightbox-close");
    close.type = "button";
    close.setAttribute("aria-label", "Close");
    const prev = text("button", "‹", "sl-reviews__lightbox-nav is-prev");
    prev.type = "button";
    const next = text("button", "›", "sl-reviews__lightbox-nav is-next");
    next.type = "button";
    const render = () => {
      img.src = urls[index];
      img.alt = root.dataset.photoAltLabel || "Customer review photo";
    };
    const dismiss = () => overlay.remove();
    close.addEventListener("click", dismiss);
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) dismiss();
    });
    prev.addEventListener("click", () => {
      index = (index - 1 + urls.length) % urls.length;
      render();
    });
    next.addEventListener("click", () => {
      index = (index + 1) % urls.length;
      render();
    });
    overlay.append(close, prev, img, next);
    document.body.append(overlay);
    render();
  };

  const renderReview = (root, review, settings, state) => {
    const showPhotos = root.dataset.slShowPhotos
      ? readFlag(root, "slShowPhotos", true)
      : settings?.show_photos !== false;
    const showDates = readFlag(root, "slShowDates", true);
    const showAuthor = root.dataset.slShowAuthor
      ? readFlag(root, "slShowAuthor", true)
      : settings?.show_author !== false;
    const showTitles = root.dataset.slShowTitles
      ? readFlag(root, "slShowTitles", true)
      : settings?.show_review_titles !== false;

    const card = document.createElement("article");
    card.className = "sl-reviews__card";
    card.dataset.reviewId = review.id;
    const masonry = isMasonryMode(root, settings);
    const urls =
      showPhotos && Array.isArray(review.media)
        ? review.media.map((item) => item.cdn_url).filter(Boolean)
        : [];

    const formatDate = (numeric = false) => {
      if (!showDates || !review.published_at) return "";
      try {
        return new Intl.DateTimeFormat(
          undefined,
          numeric
            ? { month: "2-digit", day: "2-digit", year: "numeric" }
            : { month: "short", day: "numeric", year: "numeric" },
        ).format(new Date(review.published_at));
      } catch {
        return "";
      }
    };

    if (masonry) {
      if (urls.length) {
        const hero = document.createElement("button");
        hero.type = "button";
        hero.className = "sl-reviews__card-hero";
        const image = document.createElement("img");
        image.loading = "lazy";
        image.src = urls[0];
        image.alt = root.dataset.photoAltLabel || "Customer review photo";
        hero.append(image);
        const badge = text(
          "span",
          String(urls.length),
          "sl-reviews__photo-badge",
        );
        badge.setAttribute("aria-hidden", "true");
        hero.append(badge);
        hero.addEventListener("click", () => openLightbox(root, urls, 0));
        card.append(hero);
      }

      const metaRow = document.createElement("div");
      metaRow.className = "sl-reviews__card-meta-row";
      if (showAuthor && review.author_display_name) {
        metaRow.append(
          text("strong", review.author_display_name, "sl-reviews__card-name"),
        );
      }
      const date = formatDate(true);
      if (date) metaRow.append(text("span", date, "sl-reviews__card-meta"));
      if (metaRow.childElementCount) card.append(metaRow);

      const rating = text(
        "span",
        stars(review.rating),
        "sl-reviews__card-stars",
      );
      rating.setAttribute("aria-label", `${review.rating} out of 5 stars`);
      card.append(rating);
      card.append(truncateBody(root, review.body || ""));
      return card;
    }

    const rating = text(
      "span",
      stars(review.rating),
      "sl-reviews__card-stars",
    );
    rating.setAttribute("aria-label", `${review.rating} out of 5 stars`);
    card.append(rating);

    if (showTitles && review.title)
      card.append(text("h3", review.title, "sl-reviews__card-title"));

    card.append(truncateBody(root, review.body || ""));

    if (urls.length) {
      const media = document.createElement("div");
      media.className = "sl-reviews__media";
      urls.forEach((url, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "sl-reviews__media-btn";
        const image = document.createElement("img");
        image.loading = "lazy";
        image.src = url;
        image.alt = root.dataset.photoAltLabel || "Customer review photo";
        button.append(image);
        button.addEventListener("click", () => openLightbox(root, urls, index));
        media.append(button);
      });
      if (media.childElementCount) card.append(media);
    }

    if (showAuthor && review.author_display_name) {
      const authorRow = document.createElement("div");
      authorRow.className = "sl-reviews__card-author";
      authorRow.append(
        text("strong", review.author_display_name, "sl-reviews__card-name"),
      );
      card.append(authorRow);
    }

    const date = formatDate();
    if (date) {
      card.append(text("p", date, "sl-reviews__card-meta"));
    }
    return card;
  };

  const renderSummary = (root, summary, settings, photos) => {
    const mount = root.querySelector("[data-sl-reviews-summary]");
    if (!mount) return;
    mount.replaceChildren();
    const count = Number(summary?.count || 0);
    if (!count) {
      mount.hidden = true;
      return;
    }
    mount.hidden = false;
    const showHistogram = settings?.show_histogram !== false;
    const showRecommend = settings?.show_recommend_percent !== false;
    const masonry = isMasonryMode(root, settings);
    const headerWrite = root.querySelector(
      ".sl-reviews__header [data-sl-write-review]",
    );
    if (headerWrite) headerWrite.hidden = masonry;

    const grid = document.createElement("div");
    grid.className = masonry
      ? "sl-reviews__summary-grid sl-reviews__summary-grid--masonry"
      : "sl-reviews__summary-grid";

    const left = document.createElement("div");
    left.className = "sl-reviews__summary-score";
    left.append(
      text("p", String(summary.rating), "sl-reviews__summary-rating"),
    );
    left.append(fractionalStars(summary.rating));
    left.append(
      text(
        "p",
        masonry
          ? (
              root.dataset.reviewCountLabel || "__COUNT__ reviews"
            ).replace(
              /__COUNT__|\{\{\s*count\s*\}\}/g,
              String(count),
            )
          : (root.dataset.basedOnLabel || "Based on __COUNT__ reviews").replace(
              /__COUNT__|\{\{\s*count\s*\}\}/g,
              String(count),
            ),
        "sl-reviews__summary-based",
      ),
    );
    if (!masonry && showRecommend) {
      left.append(
        text(
          "p",
          (
            root.dataset.recommendLabel ||
            "__PERCENT__% would recommend this product"
          ).replace(
            /__PERCENT__|\{\{\s*percent\s*\}\}/g,
            String(summary.recommendPercent || 0),
          ),
          "sl-reviews__summary-recommend",
        ),
      );
    }
    const write = root.querySelector("[data-sl-write-review]");
    if (!masonry && write && !write.hidden) {
      const clone = write.cloneNode(true);
      clone.hidden = false;
      clone.addEventListener("click", () => write.click());
      left.append(clone);
    }
    grid.append(left);

    if (showHistogram) {
      const middle = document.createElement("div");
      middle.className = "sl-reviews__histogram";
      const distribution = summary.distribution || {};
      for (let star = 5; star >= 1; star -= 1) {
        const n = Number(distribution[String(star)] || 0);
        const pct = count ? Math.round((n / count) * 100) : 0;
        const row = document.createElement("button");
        row.type = "button";
        row.className = "sl-reviews__histogram-row";
        row.dataset.rating = String(star);
        row.append(text("span", `${star} ★`, "sl-reviews__histogram-label"));
        const track = document.createElement("span");
        track.className = "sl-reviews__histogram-track";
        const bar = document.createElement("span");
        bar.className = "sl-reviews__histogram-bar";
        bar.style.width = `${pct}%`;
        track.append(bar);
        row.append(track);
        row.append(
          text(
            "span",
            masonry ? `(${n})` : `${pct}%`,
            "sl-reviews__histogram-pct",
          ),
        );
        row.addEventListener("click", () => {
          const filter = root.querySelector("[data-sl-filter-rating]");
          if (filter) {
            filter.value = String(star);
            filter.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
        middle.append(row);
      }
      grid.append(middle);
    }

    if (masonry) {
      const mediaCol = document.createElement("div");
      mediaCol.className = "sl-reviews__summary-media";
      const urls = Array.isArray(photos)
        ? photos.map((item) => item.cdn_url).filter(Boolean)
        : [];
      if (urls.length && settings?.show_photos !== false) {
        const thumbs = document.createElement("div");
        thumbs.className = "sl-reviews__summary-thumbs";
        urls.slice(0, 8).forEach((url, index) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "sl-reviews__photos-thumb";
          const img = document.createElement("img");
          img.src = url;
          img.alt = root.dataset.photoAltLabel || "Customer review photo";
          img.loading = "lazy";
          button.append(img);
          button.addEventListener("click", () =>
            openLightbox(root, urls, index),
          );
          thumbs.append(button);
        });
        mediaCol.append(thumbs);
      }
      if (write && settings?.show_write_review !== false) {
        const clone = write.cloneNode(true);
        clone.hidden = false;
        clone.classList.add("is-solid");
        clone.addEventListener("click", () => write.click());
        mediaCol.append(clone);
      }
      grid.append(mediaCol);
    }

    mount.append(grid);
  };

  const renderHighlights = (root, highlights, summary) => {
    const mount = root.querySelector("[data-sl-reviews-highlights]");
    if (!mount) return;
    mount.replaceChildren();
    if (!Array.isArray(highlights) || !highlights.length) {
      mount.hidden = true;
      return;
    }
    mount.hidden = false;
    mount.append(
      text(
        "h3",
        root.dataset.highlightsHeading || "What customers are saying",
        "sl-reviews__highlights-heading",
      ),
    );
    const list = document.createElement("div");
    list.className = "sl-reviews__highlights-grid";
    highlights.forEach((item) => {
      const card = document.createElement("article");
      card.className = "sl-reviews__highlight";
      if (item.title) card.append(text("h4", item.title, "sl-reviews__highlight-title"));
      if (item.body) card.append(text("p", item.body, "sl-reviews__highlight-body"));
      list.append(card);
    });
    mount.append(list);
    mount.append(
      text(
        "p",
        (
          root.dataset.highlightsDisclaimer ||
          "AI-generated summary based on __COUNT__ customer reviews."
        ).replace(
          /__COUNT__|\{\{\s*count\s*\}\}/g,
          String(summary?.count || 0),
        ),
        "sl-reviews__highlights-disclaimer",
      ),
    );
  };

  const renderPhotoStrip = (root, photos, settings) => {
    const mount = root.querySelector("[data-sl-reviews-photos]");
    if (!mount) return;
    mount.replaceChildren();
    if (isMasonryMode(root, settings)) {
      mount.hidden = true;
      return;
    }
    if (!Array.isArray(photos) || !photos.length) {
      mount.hidden = true;
      return;
    }
    mount.hidden = false;
    const head = document.createElement("div");
    head.className = "sl-reviews__photos-head";
    head.append(
      text(
        "h3",
        root.dataset.customerPhotosLabel || "Customer photos",
        "sl-reviews__photos-heading",
      ),
    );
    mount.append(head);
    const row = document.createElement("div");
    row.className = "sl-reviews__photos-row";
    const urls = photos.map((item) => item.cdn_url).filter(Boolean);
    urls.slice(0, 8).forEach((url, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "sl-reviews__photos-thumb";
      const img = document.createElement("img");
      img.src = url;
      img.alt = root.dataset.photoAltLabel || "Customer review photo";
      img.loading = "lazy";
      button.append(img);
      button.addEventListener("click", () => openLightbox(root, urls, index));
      row.append(button);
    });
    if (urls.length > 8) {
      const more = text(
        "button",
        (root.dataset.viewAllPhotosLabel || "View all __COUNT__").replace(
          /__COUNT__|\{\{\s*count\s*\}\}/g,
          String(urls.length),
        ),
        "sl-reviews__photos-more",
      );
      more.type = "button";
      more.addEventListener("click", () => openLightbox(root, urls, 0));
      row.append(more);
    }
    mount.append(row);
  };

  const renderToolbar = (root, summary, state, reload) => {
    const mount = root.querySelector("[data-sl-reviews-toolbar]");
    if (!mount) return;
    mount.replaceChildren();
    const count = Number(summary?.count || 0);
    const masonry = isMasonryMode(root, state.settings);
    const left = document.createElement("div");
    left.className = "sl-reviews__toolbar-left";

    if (!masonry) {
      left.append(
        text(
          "p",
          (root.dataset.reviewCountLabel || "__COUNT__ reviews").replace(
            /__COUNT__|\{\{\s*count\s*\}\}/g,
            String(count),
          ),
          "sl-reviews__toolbar-count",
        ),
      );

      const ratingSelect = document.createElement("select");
      ratingSelect.className = "sl-reviews__filter";
      ratingSelect.dataset.slFilterRating = "1";
      [
        ["", root.dataset.allRatingsLabel || "All ratings"],
        ["5", "5 stars"],
        ["4", "4 stars"],
        ["3", "3 stars"],
        ["2", "2 stars"],
        ["1", "1 star"],
      ].forEach(([value, label]) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = label;
        if (String(state.rating || "") === value) option.selected = true;
        ratingSelect.append(option);
      });
      ratingSelect.addEventListener("change", () => {
        state.rating = ratingSelect.value;
        state.page = 0;
        reload(true);
      });

      const photosToggle = document.createElement("button");
      photosToggle.type = "button";
      photosToggle.className = "sl-reviews__chip";
      photosToggle.classList.toggle("is-active", state.photos);
      photosToggle.textContent = root.dataset.withPhotosLabel || "With photos";
      photosToggle.addEventListener("click", () => {
        state.photos = !state.photos;
        state.page = 0;
        reload(true);
      });

      left.append(ratingSelect, photosToggle);
    }

    const right = document.createElement("div");
    right.className = "sl-reviews__toolbar-right";
    right.append(
      text(
        "label",
        root.dataset.sortLabel || (masonry ? "Sort reviews" : "Sort by"),
        "sl-reviews__sort-label",
      ),
    );
    const sortSelect = document.createElement("select");
    sortSelect.className = "sl-reviews__filter";
    [
      ["relevant", root.dataset.sortRelevantLabel || "Most relevant"],
      ["newest", root.dataset.sortNewestLabel || "Newest"],
      ["highest", root.dataset.sortHighestLabel || "Highest rated"],
      ["lowest", root.dataset.sortLowestLabel || "Lowest rated"],
      ["helpful", root.dataset.sortHelpfulLabel || "Most helpful"],
    ].forEach(([value, label]) => {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = label;
      if (state.sort === value) option.selected = true;
      sortSelect.append(option);
    });
    sortSelect.addEventListener("change", () => {
      state.sort = sortSelect.value;
      state.page = 0;
      reload(true);
    });
    right.append(sortSelect);

    if (masonry) {
      mount.classList.add("sl-reviews__toolbar--masonry");
      mount.append(right);
    } else {
      mount.classList.remove("sl-reviews__toolbar--masonry");
      mount.append(left, right);
    }
    mount.hidden = count === 0;
  };

  const mountForm = (root) => {
    if (root.dataset.slFormBound === "1") return;
    const form = root.querySelector("[data-sl-review-form]");
    const modal = root.querySelector("[data-sl-review-modal]");
    const writeButtons = root.querySelectorAll("[data-sl-write-review]");
    if (!form || !writeButtons.length) return;
    root.dataset.slFormBound = "1";

    const openForm = () => {
      if (modal) {
        modal.hidden = false;
        modal.classList.add("is-open");
        document.body.classList.add("sl-reviews-modal-open");
      } else {
        form.style.display = "";
        form.removeAttribute("hidden");
      }
    };
    const closeForm = () => {
      if (modal) {
        modal.hidden = true;
        modal.classList.remove("is-open");
        document.body.classList.remove("sl-reviews-modal-open");
      } else {
        form.style.display = "none";
      }
    };

    writeButtons.forEach((button) =>
      button.addEventListener("click", openForm),
    );
    modal
      ?.querySelector("[data-sl-review-modal-close]")
      ?.addEventListener("click", closeForm);
    modal?.addEventListener("click", (event) => {
      if (event.target === modal) closeForm();
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const status = form.querySelector("[data-sl-form-status]");
      const data = new FormData(form);
      if (data.get("website")) return;
      const body = String(data.get("body") || "").trim();
      if (!body) {
        if (status)
          status.textContent =
            root.dataset.submitErrorLabel ||
            "Unable to submit your review right now.";
        return;
      }
      try {
        const response = await fetch(`${REVIEWS_API_BASE}/reviews-storefront`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            shop: root.dataset.shop,
            product: root.dataset.product,
            rating: Number(data.get("rating") || 5),
            title: String(data.get("title") || ""),
            body,
            author: String(data.get("author") || ""),
            website: String(data.get("website") || ""),
          }),
        });
        if (!response.ok) throw new Error();
        form.reset();
        closeForm();
        if (status)
          status.textContent =
            root.dataset.submitSuccessLabel ||
            "Thanks — your review was submitted for moderation.";
      } catch {
        if (status)
          status.textContent =
            root.dataset.submitErrorLabel ||
            "Unable to submit your review right now.";
      }
    });
  };

  const formatCountLabel = (root, count) => {
    const n = Number(count) || 0;
    const template =
      n === 1
        ? root.dataset.countLabelOne || "__COUNT__ review"
        : root.dataset.countLabelOther || "__COUNT__ reviews";
    return template.replace(/__COUNT__|\{\{\s*count\s*\}\}/g, String(n));
  };

  const formatRatingValue = (rating) => {
    const value = Math.max(0, Math.min(5, Number(rating) || 0));
    return (Math.round(value * 10) / 10).toFixed(1);
  };

  const summaryDivider = () => {
    const divider = document.createElement("span");
    divider.className = "sl-reviews__summary-divider";
    divider.setAttribute("aria-hidden", "true");
    return divider;
  };

  const formatBadgeAria = (root, rating, count) => {
    const value = Math.round((Number(rating) || 0) * 10) / 10;
    const template =
      root.dataset.badgeAriaLabel ||
      "__RATING__ out of 5 from __COUNT__ reviews";
    return template
      .replace(/__RATING__|\{\{\s*rating\s*\}\}/g, String(value))
      .replace(/__COUNT__|\{\{\s*count\s*\}\}/g, String(Number(count) || 0));
  };

  /** Compact layouts (Star rating / Badge): hydrate from reviews-storefront summary. */
  const mountCompact = (root) => {
    const widget = root.dataset.slWidget;
    if (widget !== "stars" && widget !== "badge") return false;
    if (!root.dataset.shop || !root.dataset.product) return true;

    const applySummary = (summary) => {
      const rating = Number(summary?.rating || 0);
      const count = Number(summary?.count || 0);
      const rounded = Math.round(rating * 10) / 10;
      root.dataset.initialRating = String(rounded);
      root.dataset.initialCount = String(count);

      if (widget === "stars") {
        const content = root.querySelector("[data-sl-stars-content]") || root;
        content.replaceChildren();
        if (count > 0) {
          root.setAttribute("aria-label", formatBadgeAria(root, rounded, count));
          content.append(fractionalStars(rating));
          content.append(
            text("span", formatRatingValue(rounded), "sl-reviews__score"),
          );
          content.append(summaryDivider());
          const link = text(
            "a",
            formatCountLabel(root, count),
            "sl-reviews__count",
          );
          link.href = "#storelaunch-reviews";
          content.append(link);
        } else {
          // No reviews yet: show nothing at all.
          root.style.display = "none";
        }
        if (count > 0) root.style.display = "";
        return;
      }

      const content = root.querySelector("[data-sl-badge-content]") || root;
      content.replaceChildren();
      if (count > 0) {
        const link = document.createElement("a");
        link.className = "sl-reviews sl-reviews--badge";
        link.href = "#storelaunch-reviews";
        link.setAttribute("aria-label", formatBadgeAria(root, rounded, count));
        link.append(fractionalStars(rating));
        link.append(
          text("span", `${rounded} · ${formatCountLabel(root, count)}`),
        );
        content.append(link);
        root.style.display = "";
      } else {
        // No reviews yet: show nothing at all.
        root.style.display = "none";
      }
    };

    const load = async () => {
      try {
        const url = new URL(`${REVIEWS_API_BASE}/reviews-storefront`);
        url.searchParams.set("shop", root.dataset.shop);
        url.searchParams.set("product", root.dataset.product);
        url.searchParams.set("page", "0");
        // Keep this entitlement-sensitive request off any pre-gate CDN key.
        url.searchParams.set("reviews_gate", "1");
        const response = await fetch(url, {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error();
        const payload = await response.json();
        if (payload.settings) applySettings(root, payload.settings);
        if (payload.summary) applySummary(payload.summary);
      } catch {
        // Never leave server-rendered Liquid stars/badge visible when the
        // entitlement-sensitive request fails. A stale summary is worse than
        // a temporarily hidden widget.
        root.style.display = "none";
        if (
          !root.dataset.initialCount ||
          Number(root.dataset.initialCount) <= 0
        ) {
          const content =
            root.querySelector(
              "[data-sl-stars-content], [data-sl-badge-content]",
            ) || root;
          if (
            !content.querySelector(
              ".sl-reviews__score, .sl-reviews--badge",
            )
          ) {
            content.replaceChildren(
              text(
                "span",
                root.dataset.unavailableLabel ||
                  "Reviews are unavailable right now.",
                "sl-reviews__empty",
              ),
            );
          }
        }
      }
    };

    // Badge is viewport-fixed — load immediately. Roots start display:none so
    // they cannot paint baked Liquid summaries while entitlement is checked;
    // hidden roots are not observable, so hydrate them immediately.
    if (
      widget === "badge" ||
      root.style.display === "none" ||
      typeof IntersectionObserver === "undefined"
    ) {
      load();
      return true;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(root);
    return true;
  };

  const mount = (root) => {
    if (mountCompact(root)) return;

    const list = root.querySelector(".sl-reviews__items");
    const loading = root.querySelector(".sl-reviews__loading");
    const pager = root.querySelector(".sl-reviews__pager");
    if (!list) return;
    applyLayoutClass(root, resolveLayout(root, null), null);

    const state = {
      page: 0,
      total: 0,
      busy: false,
      settings: null,
      summary: null,
      rating: "",
      photos: false,
      sort: "",
    };

    const load = async (reset = false) => {
      if (state.busy) return;
      state.busy = true;
      if (reset) {
        state.page = 0;
        list.replaceChildren();
      }
      if (loading) loading.hidden = false;
      try {
        const url = new URL(`${REVIEWS_API_BASE}/reviews-storefront`);
        url.searchParams.set("shop", root.dataset.shop);
        url.searchParams.set("product", root.dataset.product);
        url.searchParams.set("page", String(state.page));
        // Keep this entitlement-sensitive request off any pre-gate CDN key.
        url.searchParams.set("reviews_gate", "1");
        if (state.sort) url.searchParams.set("sort", state.sort);
        if (state.rating) url.searchParams.set("rating", state.rating);
        if (state.photos) url.searchParams.set("photos", "1");
        const response = await fetch(url, {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error();
        const payload = await response.json();
        state.total = payload.total || 0;
        state.settings = payload.settings || state.settings;
        state.summary = payload.summary || state.summary;
        if (!state.sort && state.settings?.default_sort)
          state.sort = state.settings.default_sort;
        applySettings(root, state.settings);
        mountForm(root);
        if (state.page === 0) {
          renderSummary(
            root,
            state.summary,
            state.settings,
            payload.photos || [],
          );
          renderHighlights(root, payload.highlights || [], state.summary);
          renderPhotoStrip(root, payload.photos || [], state.settings);
          renderToolbar(root, state.summary, state, load);
          // Hide the entire reviews section (heading and all) until the
          // product has at least one published review. Filters never zero
          // out summary.count, so this only fires for truly review-less
          // products.
          const overallCount = Number(state.summary?.count || 0);
          root.style.display = overallCount === 0 ? "none" : "";
        }
        (payload.reviews || []).forEach((review) =>
          list.append(renderReview(root, review, state.settings, state)),
        );
        state.page += 1;
        if (loading) loading.hidden = true;
        if (pager) {
          pager.replaceChildren();
          if (!list.childElementCount)
            list.append(
              text(
                "p",
                root.dataset.noResultsLabel || "No published reviews yet.",
                "sl-reviews__no-results",
              ),
            );
          if (list.childElementCount < state.total) {
            const button = text(
              "button",
              root.dataset.loadMoreLabel || "Load more reviews",
              "sl-reviews__more",
            );
            button.type = "button";
            if (state.settings?.button_style === "solid")
              button.classList.add("is-solid");
            if (state.settings?.button_style === "text")
              button.classList.add("is-text");
            button.addEventListener("click", () => load(false));
            pager.append(button);
          }
        }
      } catch {
        // Keep stale server-rendered review content hidden on entitlement or
        // network failures; it is safer to retry on a later page load.
        root.style.display = "none";
        if (loading)
          loading.textContent =
            root.dataset.unavailableLabel ||
            "Reviews are unavailable right now.";
      } finally {
        state.busy = false;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load(true);
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(root);
  };

  document.querySelectorAll("[data-sl-reviews-root]").forEach(mount);

})();
